import { FormEvent, useEffect, useRef, useState } from "react";
import { UserRound, X } from "lucide-react";

type SpeakerNameDialogProps = {
  open: boolean;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onSave: (name: string) => void;
};

export function SpeakerNameDialog({
  open,
  busy = false,
  error = null,
  onCancel,
  onSave
}: SpeakerNameDialogProps) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [name, setName] = useState("");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
      dialog.querySelector<HTMLInputElement>("input")?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = name.trim();
    if (!value || busy) return;
    onSave(value);
  };

  if (!open) return null;

  return (
    <dialog
      ref={dialogRef}
      className="speaker-name-dialog"
      aria-labelledby="speaker-name-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onCancel();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <form className="speaker-name-dialog__panel" onSubmit={submit}>
        <header>
          <span className="icon-frame" aria-hidden="true">
            <UserRound size={18} />
          </span>
          <div>
            <h2 id="speaker-name-title">What should we call you in transcripts?</h2>
            <p>This name becomes the default speaker on new recordings.</p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close speaker name prompt"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        <div className="field">
          <label htmlFor="speaker-display-name">Display name</label>
          <input
            id="speaker-display-name"
            value={name}
            maxLength={120}
            autoComplete="name"
            onChange={(event) => setName(event.target.value)}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "speaker-name-error" : "speaker-name-help"}
          />
          <small id={error ? "speaker-name-error" : "speaker-name-help"}>
            {error ?? "You can change this later in account settings."}
          </small>
        </div>
        <button
          className="button button--primary"
          type="submit"
          disabled={busy || !name.trim()}
          aria-busy={busy}
        >
          {busy ? "Saving…" : "Save and continue"}
        </button>
      </form>
    </dialog>
  );
}
